import "./interview-demo.css";

import { LiveKitRoom } from "@livekit/components-react";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Lock,
  Mic,
  MicOff,
  RotateCcw,
  Video,
  VideoOff,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { track } from "@/lib/analytics";
import { currentReplayUrl } from "@/lib/analytics/posthog";
import { ctaDestination, trackEvent } from "@/lib/analytics/tracking";
import { apex25LegalUrl, demoResultsUrl } from "@/lib/apex25AppUrl";

import { type CallEndReason, DemoCall } from "./DemoCall";
import {
  DemoApiError,
  type DemoRole,
  type DemoSession,
  fetchDemoRoles,
  fetchDemoStatus,
  reportDemoNoAgent,
  saveDemoTranscript,
  startDemoSession,
  type TranscriptRow,
} from "./demo-api";
import { DemoViewMap } from "./DemoViewMap";
import { formatTimeLeft, readLastDemo, rememberLastDemo, useNow } from "./last-demo";
import { MobileNextStep } from "./MobileNextStep";
import { callMicOptions, findPhoneAudio, type PhoneAudio } from "./phone-audio";
import { RecruiterSend } from "./RecruiterSend";
import { SelfView } from "./SelfView";
import { SignInToSeeResults } from "./SignInToSeeResults";
import { isDemoSupported, useSelfView } from "./use-self-view";

type ErrorKind =
  | "mic-blocked"
  | "unsupported"
  | "connect"
  | "no-agent"
  | "no-answers"
  | "dropped"
  | "busy"
  | "disabled"
  | "save";

type Phase =
  | { name: "send" }
  | { name: "check" }
  | { name: "connecting" }
  | { name: "live"; session: DemoSession }
  | { name: "submitting" }
  // The call is over and saved: the results (review + Ask AI) are on recruit.theresume.io,
  // behind sign-in.
  | { name: "signin"; sessionId: string }
  | { name: "error"; kind: ErrorKind }
  | { name: "locked"; retryAt: number };

const ACTS = ["Send the interview", "Take the interview", "Review it", "Ask the AI"];

const actIndex = (phase: Phase) => {
  switch (phase.name) {
    case "send": {
      return 0;
    }
    case "signin": {
      return 2;
    }
    default: {
      return 1;
    }
  }
};

const ERRORS: Record<ErrorKind, { title: string; body: string; retry: boolean }> = {
  "mic-blocked": {
    title: "We need your mic to start",
    body: "Allow microphone access in your browser settings, then try again.",
    retry: true,
  },
  unsupported: {
    title: "Your browser can't run the interview",
    body: "Try the latest Chrome, Safari, Edge, or Firefox.",
    retry: false,
  },
  connect: {
    title: "We couldn't connect",
    body: "Check your connection and try again.",
    retry: true,
  },
  "no-agent": {
    title: "Our AI interviewer isn't available right now",
    body: "Sorry for the inconvenience. Please try again in a few minutes, or book a demo and we'll walk you through it.",
    retry: true,
  },
  "no-answers": {
    title: "We didn't hear any answers",
    body: "Your results are built from your answers. Check that your microphone is on and unmuted, then try again.",
    retry: true,
  },
  dropped: {
    title: "The call dropped",
    body: "Check your connection and try again.",
    retry: true,
  },
  busy: {
    title: "The demo is busy right now",
    body: "Try again in a few minutes, or book a demo with our team.",
    retry: true,
  },
  disabled: {
    title: "The live demo is taking a break",
    body: "Book a demo and we'll walk you through it.",
    retry: false,
  },
  save: {
    title: "We couldn't save your interview",
    body: "Your results need to be saved before you can see them. Please try the interview again.",
    retry: true,
  },
};

const minutesUntil = (timestamp: number) =>
  Math.max(Math.ceil((timestamp - Date.now()) / 60_000), 1);

type Props = { bookingUrl: string };

export const InterviewDemo = ({ bookingUrl }: Props) => {
  const trackBooking = () =>
    trackEvent("cta_clicked", {
      cta_id: "book_demo",
      cta_location: "apex25_interview_demo",
      destination: ctaDestination(bookingUrl),
    });
  const [roles, setRoles] = useState<DemoRole[] | null>(null);
  const [rolesFailed, setRolesFailed] = useState(false);
  const [role, setRole] = useState<DemoRole | null>(null);
  const [sent, setSent] = useState(false);
  const [phase, setPhase] = useState<Phase>({ name: "send" });
  const [remaining, setRemaining] = useState<number | null>(null);
  const [transcript, setTranscript] = useState<TranscriptRow[]>([]);
  const [, setClock] = useState(0);
  // Read at the end of the call, which fires from LiveKit callbacks outside React's render.
  const sessionIdRef = useRef<string | null>(null);
  const transcriptRef = useRef<TranscriptRow[]>([]);
  transcriptRef.current = transcript;

  const selfView = useSelfView();
  const { stop: stopSelfView } = selfView;
  const secondsLeftRef = useRef(0);
  const maxSecondsRef = useRef(120);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const [phoneAudio, setPhoneAudio] = useState<PhoneAudio | null>(null);
  const callMic = useMemo(
    () => callMicOptions(selfView.micOn, phoneAudio),
    [selfView.micOn, phoneAudio]
  );
  const frameRef = useRef<HTMLDivElement>(null);
  const userMovedRef = useRef(false);

  const go = useCallback((next: Phase) => {
    userMovedRef.current = true;
    // Update the ref now: leaving "live" unmounts the room, which fires onDisconnected before
    // the next render would refresh it.
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Keep the demo in view and move focus to the new step for keyboard and screen reader users.
  useEffect(() => {
    if (!userMovedRef.current) return;
    const frame = frameRef.current;
    if (frame && frame.getBoundingClientRect().top < 0) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      frame.scrollIntoView({ block: "start", behavior: reduced ? "auto" : "smooth" });
    }
    frame?.querySelector<HTMLElement>(".a25-stage h3")?.focus({ preventScroll: true });
  }, [phase.name]);

  useEffect(() => {
    fetchDemoRoles()
      .then((loaded) => {
        setRoles(loaded);
        setRole(loaded[0] ?? null);
      })
      .catch(() => setRolesFailed(true));

    fetchDemoStatus()
      .then((status) => {
        setRemaining(status.remaining);
        if (status.enabled === false) setPhase({ name: "error", kind: "disabled" });
      })
      .catch(() => undefined);
  }, []);

  // Keep the soft-lock countdown fresh, and unlock when the hour is up.
  useEffect(() => {
    if (phase.name !== "locked") return;
    const interval = window.setInterval(() => {
      if (Date.now() >= phase.retryAt) {
        setRemaining(null);
        setSent(false);
        setPhase({ name: "send" });
      } else setClock((tick) => tick + 1);
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [phase]);

  const fail = useCallback(
    (kind: ErrorKind) => {
      track("apex25_demo_failed", { reason: kind, role: role?.id });
      stopSelfView();
      go({ name: "error", kind });
    },
    [go, stopSelfView, role?.id]
  );

  const send = () => {
    if (!role) return;
    setSent(true);
    track("apex25_demo_sent", { role: role.id });
  };

  const openInterview = async () => {
    if (!isDemoSupported()) return fail("unsupported");
    go({ name: "check" });
    const problem = await selfView.start();
    if (problem) fail(problem);
  };

  const startInterview = async () => {
    if (!role) return;
    go({ name: "connecting" });
    try {
      // After the mic check, so the phone's audio routes have names.
      setPhoneAudio(await findPhoneAudio());
      const session = await startDemoSession(role.id);
      setRemaining(session.remaining);
      sessionIdRef.current = session.sessionId;
      setTranscript([]);
      maxSecondsRef.current = session.maxSeconds;
      selfView.releaseAudio();
      trackEvent("trial_started", { trial: "interview_demo", role: role.id });
      go({ name: "live", session });
    } catch (error) {
      const code = error instanceof DemoApiError ? error.code : "unknown";
      if (code === "demo_rate_limited") {
        setRemaining(0);
        stopSelfView();
        const retryAfter = error instanceof DemoApiError ? error.retryAfterSeconds ?? 3600 : 3600;
        go({ name: "locked", retryAt: Date.now() + retryAfter * 1000 });
      } else if (code === "demo_busy") fail("busy");
      else if (code === "demo_disabled") fail("disabled");
      else fail("connect");
    }
  };

  const finishCall = useCallback(
    (reason: CallEndReason) => {
      if (phaseRef.current.name !== "live") return;
      trackEvent("trial_completed", { trial: "interview_demo", role: role?.id, reason });
      stopSelfView();
      const sessionId = sessionIdRef.current;
      const seconds = Math.max(maxSecondsRef.current - Math.max(secondsLeftRef.current, 0), 0);
      if (!transcriptRef.current.some((row) => row.speaker === "candidate" && row.text.trim())) {
        // Nothing to review, so no sign-in step.
        fail("no-answers");
        return;
      }
      go({ name: "submitting" });
      if (!sessionId) {
        fail("save");
        return;
      }
      // Save first, then offer sign-in: the results page reads the transcript from the server.
      // Keep "Interview submitted" on screen for a moment even when the save is instant.
      const minimumWait = new Promise((resolve) => window.setTimeout(resolve, 1200));
      Promise.all([
        saveDemoTranscript(sessionId, transcriptRef.current, seconds, currentReplayUrl()),
        minimumWait,
      ])
        .then(([saved]) => {
          // So a visitor who leaves can come back to "View your results" for as long as they're kept.
          rememberLastDemo({
            sessionId,
            roleId: role?.id ?? "",
            expiresAt: saved.expiresAt ?? new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
            auth: "signup",
          });
          track("apex25_demo_results_gate", { role: role?.id });
          go({ name: "signin", sessionId });
        })
        .catch(() => fail("save"));
    },
    [fail, go, stopSelfView, role?.id]
  );

  const handleNoAgent = useCallback(() => {
    if (phaseRef.current.name !== "live") return;
    const sessionId = sessionIdRef.current;
    // Sent before leaving the room, which the server checks. When the attempt is given back,
    // refresh the "demos left" count.
    if (sessionId) {
      reportDemoNoAgent(sessionId)
        .then(({ refunded }) => (refunded ? fetchDemoStatus() : null))
        .then((status) => {
          if (status) setRemaining(status.remaining);
        })
        .catch(() => undefined);
    }
    fail("no-agent");
  }, [fail]);

  const handleDisconnected = useCallback(() => {
    if (phaseRef.current.name !== "live") return;
    // The worker deletes the room when time is up; a disconnect near the end is a normal finish.
    if (secondsLeftRef.current <= 20) finishCall("time_up");
    else fail("dropped");
  }, [fail, finishCall]);

  const startOver = () => {
    stopSelfView();
    setSent(false);
    setTranscript([]);
    sessionIdRef.current = null;
    go({ name: "send" });
  };

  // Before the early returns below: hooks must run on every render.
  const lastDemo = phase.name === "send" && !sent ? readLastDemo() : null;
  const now = useNow(Boolean(lastDemo));

  if (rolesFailed) {
    return (
      <div className="a25-demo a25-demo-message">
        <p>The live demo isn&apos;t available right now.</p>
        <a className="btn" href={bookingUrl} target="_blank" rel="noopener" onClick={trackBooking}>
          Book a demo <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>
    );
  }

  if (!roles || !role) {
    return (
      <div className="a25-demo a25-demo-message" aria-busy="true">
        <Loader2 size={18} className="a25-spin" aria-hidden="true" /> Loading the demo…
      </div>
    );
  }

  const current = actIndex(phase);
  const interviewDone = phase.name === "signin";
  const candidateView = current === 1;

  return (
    <div ref={frameRef} className="a25-demo">
      <div className="a25-demo-bar">
        <ol className="a25-acts" aria-label="Demo steps">
          {ACTS.map((act, index) => (
            <li
              key={act}
              aria-current={index === current ? "step" : undefined}
              data-done={index < current}
            >
              <span className="a25-act">
                <span>{String(index + 1).padStart(2, "0")}</span>
                {act}
                {index >= 2 && (
                  <Lock size={11} aria-label="Sign in to see" className="a25-act-lock" />
                )}
              </span>
            </li>
          ))}
        </ol>
        <DemoViewMap view={candidateView ? "candidate" : "recruiter"} />
      </div>

      <div className="a25-stage" data-phase={phase.name}>
        {phase.name === "send" && (
          <>
            <h3 tabIndex={-1} className="sr-only">
              Send the interview
            </h3>
            {lastDemo && Date.parse(lastDemo.expiresAt) > now && (
              <a
                className="a25-last-demo"
                href={demoResultsUrl(lastDemo.sessionId, lastDemo.auth)}
                onClick={() => track("apex25_demo_last_results_opened", { role: lastDemo.roleId })}
              >
                <span>
                  <strong>Your results from your last demo are still here</strong>, for another{" "}
                  {formatTimeLeft(lastDemo.expiresAt, now)}.
                </span>
                <span className="a25-last-demo-cta">
                  View your results <ArrowRight size={15} aria-hidden="true" />
                </span>
              </a>
            )}
            <RecruiterSend
              roles={roles}
              role={role}
              sent={sent}
              remaining={remaining}
              onSelectRole={(next) => {
                setRole(next);
                track("apex25_demo_role", { role: next.id });
              }}
              onSend={send}
              onOpenInterview={() => void openInterview()}
            />
          </>
        )}

        {(phase.name === "check" || phase.name === "connecting") && (
          <div className="a25-dark a25-check">
            <div className="a25-check-main">
              <p className="a25-label">
                {role.company} · {role.title}
              </p>
              <h3 tabIndex={-1}>Check your camera and mic</h3>
              <SelfView
                stream={selfView.stream}
                cameraOn={selfView.cameraOn}
                className="is-large"
              />
              {selfView.requesting ? (
                <p className="a25-dark-body">
                  <Loader2 size={15} className="a25-spin" aria-hidden="true" /> Allow camera and mic
                  access to continue.
                </p>
              ) : (
                <>
                  <div className="a25-check-row">
                    <div
                      ref={(el) => {
                        selfView.meterRef.current = el;
                      }}
                      className="a25-mic-meter"
                      aria-hidden="true"
                    >
                      <span />
                    </div>
                    <span className="a25-dark-body">
                      {selfView.micOn ? "Say something. The bar should move." : "Mic is muted."}
                    </span>
                    <div className="a25-check-toggles">
                      <button
                        type="button"
                        className="a25-control"
                        aria-pressed={!selfView.micOn}
                        aria-label={selfView.micOn ? "Mute microphone" : "Unmute microphone"}
                        onClick={selfView.toggleMic}
                        disabled={phase.name === "connecting"}
                      >
                        {selfView.micOn ? <Mic size={17} /> : <MicOff size={17} />}
                      </button>
                      <button
                        type="button"
                        className="a25-control"
                        aria-pressed={!selfView.cameraOn}
                        aria-label={selfView.cameraOn ? "Turn camera off" : "Turn camera on"}
                        onClick={() => void selfView.toggleCamera()}
                      >
                        {selfView.cameraOn ? <Video size={17} /> : <VideoOff size={17} />}
                      </button>
                    </div>
                  </div>
                  <p className="a25-consent">
                    Your video stays on your device. Your answers are transcribed and kept for 2
                    hours so you can see your results after signing in, then deleted.
                  </p>
                  <div className="a25-actions">
                    <button
                      type="button"
                      className={`btn is-light${
                        phase.name === "check" && selfView.stream ? " a25-nudge" : ""
                      }`}
                      data-next-step=""
                      onClick={() => void startInterview()}
                      disabled={phase.name === "connecting" || !selfView.stream}
                    >
                      {phase.name === "connecting" ? (
                        <>
                          <Loader2 size={16} className="a25-spin" aria-hidden="true" /> Connecting
                        </>
                      ) : (
                        <>
                          Start interview <ArrowRight size={16} aria-hidden="true" />
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="a25-link is-light"
                      onClick={startOver}
                      disabled={phase.name === "connecting"}
                    >
                      <ArrowLeft size={15} aria-hidden="true" /> Back
                    </button>
                  </div>
                  <p className="a25-terms">
                    By continuing you agree to the{" "}
                    <a href={apex25LegalUrl("/trial-terms")} target="_blank" rel="noopener">
                      Apex 25 trial terms
                    </a>
                    . Our{" "}
                    <a href={apex25LegalUrl("/privacy")} target="_blank" rel="noopener">
                      Privacy Policy
                    </a>{" "}
                    explains how we handle your information.
                  </p>
                </>
              )}
            </div>
            <aside className="a25-call-questions" aria-label="Interview questions">
              <p className="a25-label">You&apos;ll be asked</p>
              <ol>
                {role.questions.map((question, index) => (
                  <li key={question}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {question}
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        )}

        {phase.name === "live" && (
          <div className="a25-dark a25-live">
            <h3 tabIndex={-1} className="sr-only">
              Interview in progress
            </h3>
            <LiveKitRoom
              serverUrl={phase.session.serverUrl}
              token={phase.session.token}
              connect
              audio={callMic}
              video={false}
              onDisconnected={handleDisconnected}
              onError={() => fail("connect")}
              onMediaDeviceFailure={() => fail("mic-blocked")}
              className="a25-room"
            >
              <DemoCall
                maxSeconds={phase.session.maxSeconds}
                questions={role.questions}
                company={role.company}
                stream={selfView.stream}
                cameraOn={selfView.cameraOn}
                phoneAudio={phoneAudio}
                secondsLeftRef={secondsLeftRef}
                onToggleCamera={() => void selfView.toggleCamera()}
                onEnded={finishCall}
                onNoAgent={handleNoAgent}
                onTranscript={setTranscript}
              />
            </LiveKitRoom>
          </div>
        )}

        {phase.name === "submitting" && (
          <div className="a25-dark a25-center">
            <h3 tabIndex={-1}>Interview submitted</h3>
            <p className="a25-dark-body">
              <Loader2 size={15} className="a25-spin" aria-hidden="true" /> Preparing your results…
            </p>
          </div>
        )}

        {phase.name === "signin" && <SignInToSeeResults role={role} sessionId={phase.sessionId} />}

        {phase.name === "error" && (
          <div className="a25-dark a25-center">
            <h3 tabIndex={-1}>{ERRORS[phase.kind].title}</h3>
            <p className="a25-dark-body">{ERRORS[phase.kind].body}</p>
            <div className="a25-actions">
              {ERRORS[phase.kind].retry && (
                <button
                  type="button"
                  className="btn is-light"
                  data-next-step=""
                  onClick={startOver}
                >
                  Try again
                </button>
              )}
              <a
                className="a25-link is-light"
                href={bookingUrl}
                target="_blank"
                rel="noopener"
                onClick={trackBooking}
              >
                Book a demo <ArrowRight size={15} aria-hidden="true" />
              </a>
            </div>
          </div>
        )}

        {phase.name === "locked" && (
          <div className="a25-dark a25-center">
            <Lock size={20} aria-hidden="true" className="a25-lock" />
            <h3 tabIndex={-1}>You&apos;ve used your free demo interviews</h3>
            <p className="a25-dark-body">
              Please try again in an hour. Or book a demo and we&apos;ll walk you through it.
            </p>
            <div className="a25-actions">
              <a
                className="btn is-light"
                href={bookingUrl}
                target="_blank"
                rel="noopener"
                onClick={trackBooking}
              >
                Book a demo <ArrowRight size={16} aria-hidden="true" />
              </a>
              <span className="a25-link is-light is-muted">
                Try again in {minutesUntil(phase.retryAt)} min
              </span>
            </div>
          </div>
        )}
      </div>

      {interviewDone && (
        <div className="a25-demo-foot">
          <button type="button" className="a25-link" onClick={startOver}>
            <RotateCcw size={14} aria-hidden="true" /> Start over with another role
          </button>
          <a
            className="btn alt"
            href={bookingUrl}
            target="_blank"
            rel="noopener"
            onClick={trackBooking}
          >
            Book a demo <ArrowRight size={16} aria-hidden="true" />
          </a>
        </div>
      )}

      <MobileNextStep
        frameRef={frameRef}
        hostSelector=".apex25"
        step={`Step ${current + 1} of ${ACTS.length} · ${ACTS[current]}`}
      />
    </div>
  );
};
